import { get, pick, values } from 'lodash';
import { asWidgetProps } from 'Src/types/widget-helpers';
import type { ComponentProps, ReactNode } from 'react';
import React, {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  useEffect,
  useState,
} from 'react';
import { batch } from 'react-redux';
import { ParticipantsErrors } from 'Cms/product-booking/participantsErrors';
import type { FormErrorsType } from 'Cms/product-booking/participants.validator';
import ParticipantsValidator from 'Cms/product-booking/participants.validator';
import { Button } from 'Components/frameworks/button/Button';
import { Notification } from 'Components/frameworks/notification/Notification';
import { Text } from 'Components/frameworks/text/Text';
import { ICONS } from 'Components/utils/icon/icons';
import { WatchLocationQueryChange } from 'Components/utils/watch-location-query-change/WatchLocationQueryChange';
import { BookingStepTitle } from 'Components/widgets/booking-step-title/BookingStepTitle';
import { Participants } from 'Components/widgets/trip-information/participants/Participants';
import {
  DEFAULT_MAX_AGE,
  DEFAULT_MIN_AGE,
} from 'Components/widgets/trip-information/participants/participants.constants';
import { useAppContext } from 'Context/ReactAppContext';
import { usePageContext } from 'Context/PageContext';
import { t } from 'Src/i18n';
import { setKidsValidationState } from 'Store/faceting/facetingSearch.action';
import { setDepartureCity } from 'Store/trip-criteria/departure-city/departureCity.action';
import { setDuration } from 'Store/trip-criteria/duration/duration.action';
import { DEFAULT_NO_DURATION } from 'Store/trip-criteria/duration/duration.constants';
import { setAdultsNumber } from 'Store/trip-information/adults/adults.action';
import { resetKids, setKids } from 'Store/trip-information/kids/kids.action';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { getFormattedKidsBirthdate } from 'Utils/trip-information/tripInformationUtils';
import { QUERY_DURATION, QUERY_TRANSPORT } from 'Utils/uri/constants';
import { trackLastVisitedProducts } from 'Tracking/last-visited-tracking-api';
import { ReassuranceBlock } from './ReassuranceBlock';
import { TransportCriteria } from './TransportCriteria';
type Props = {
  adultOnly?: boolean;
  bookingConfig: any;
  isWinterResort: boolean;
  kids: Array<any>;
  adultsNumber: number;
  duration: number;
  departureDate?: string;
  isDepartureDateAvailaible?: boolean;
  departureCity: string;
  productId: string;
  children?: ReactNode;
  childAge: number;
  // this is duplicated from i18n. But to add the <ParticipantsErrors /> here, it's simpler to do so
  // and we should not use a recomposition for the i18n anyway
  config: CMSContent;
  isJoyView?: boolean;
  isUrbanOasis?: boolean;
  isTransportEnabled?: boolean;
  pushImage?: string;
  setKids: typeof setKids;
  setKidsValidationState: typeof setKidsValidationState;
  resetKids: typeof resetKids;
  setAdultsNumber: typeof setAdultsNumber;
  setDuration: typeof setDuration;
  setDepartureCity: typeof setDepartureCity;
  getPackages: (arrivalDate?: string | null | undefined) => void;
  dataSelector: string;
  resort: Product;
  i18n: any;
  tripInformation: TripInformation;
  tripDurationCriteria?: number | null | undefined;
};

const BookingCriteriaComponent = (props: Props) => {
  const appContext = useAppContext();
  const pageContext = usePageContext();
  const {
    adultOnly,
    adultsNumber = 2,
    bookingConfig,
    childAge = 0,
    children,
    config,
    departureCity = '',
    i18n = {},
    isDepartureDateAvailaible,
    isWinterResort = false,
    kids = [],
    resort,
  } = props;
  const [departureDate, setDepartureDate] = useState(props.departureDate);
  const [tripDurationCriteria, setTripDurationCriteria] = useState(props.tripDurationCriteria);
  const [isLoading] = useState(false);
  const [hasCheckedPrice, setHasCheckedPrice] = useState(false);
  const departureCities =
    i18n?.bookingCriteria?.departureCities.map((departureCityToMap) => ({
      value: departureCityToMap?.value,
      label: departureCityToMap?.name || '',
    })) || [];
  const initialTransport = i18n?.bookingCriteria?.initialTransport || '';
  const transportIcon = resort?.transportType || '';
  const withTransport = departureCities.some(({ value }) => value !== 'NO');

  if (
    departureDate !== props.departureDate ||
    tripDurationCriteria !== props.tripDurationCriteria
  ) {
    setDepartureDate(props.departureDate);
    setHasCheckedPrice(false);
    setTripDurationCriteria(props.tripDurationCriteria);
  }

  useEffect(() => {
    const {
      tripInformation: { kids: _kids },
    } = props;

    if (adultOnly && _kids?.length) {
      props.resetKids();
    }
  }, []);

  const checkFormErrors = (): FormErrorsType => {
    const dateFormat = get(props, 'i18n.dateFormat', 'DD/MM/YYYY');
    const birthdays = getFormattedKidsBirthdate(kids, dateFormat);
    const adultAgeInYear = +get(props, 'i18n.adultAge', DEFAULT_MAX_AGE);

    const _childAge = typeof childAge === 'number' ? childAge : DEFAULT_MIN_AGE;

    const childAgeInYear = _childAge / 12;
    return ParticipantsValidator.checkFormErrors({
      adults: adultsNumber,
      children: kids.length,
      departureDate: tripDurationCriteria ? departureDate : null,
      birthdays,
      dateFormat,
      adultAge: adultAgeInYear,
      childAge: childAgeInYear,
      birthdayFormatErrorMessage: get(props, 'i18n.birthdayFormatErrorMessage'),
      adultAgeErrorMessage: get(props, 'i18n.adultAgeErrorMessage'),
      minAgeErrorMessage: get(props, 'i18n.minAgeErrorMessage'),
      isSeller: appContext.isSeller,
    });
  };

  const handleChange = (action) => {
    return (payload) => {
      setHasCheckedPrice(false);
      // $FlowExpectedError
      action(payload);
    };
  };

  const handleCheckPrice = () => {
    const haveError = values(checkFormErrors()).some((error) => error);

    if (!haveError) {
      return props.getPackages(departureDate);
    }
  };

  const onSubmit = () => {
    trackLastVisitedProducts(pageContext.pageType, {
      id: resort.resortId,
      be: true,
      timestamp: Date.now(),
    });
    setHasCheckedPrice(!!isDepartureDateAvailaible || hasCheckedPrice);

    if (isDepartureDateAvailaible) {
      handleCheckPrice();
    }

    if (tripDurationCriteria !== null && window.pageYOffset > 0) {
      window.scrollTo(0, 0);
    }
  };

  const formErrors = checkFormErrors();
  return (
    <Fragment>
      <WatchLocationQueryChange
        onChange={(value) => value && setDuration(parseInt(value, 10) || DEFAULT_NO_DURATION)}
        queryKey={QUERY_DURATION}
      />
      {withTransport && (
        <Fragment>
          <WatchLocationQueryChange
            onChange={(value) =>
              value &&
              setDepartureCity({
                value,
              })
            }
            queryKey={QUERY_TRANSPORT}
          />
          <BookingStepTitle className="mb-15" number={1} title={t('booking.form.transportFrom')} />
          <div className="mb-30 gap-x-100 md:flex">
            <div className="mb-20 md:mb-0 md:flex-1">
              <TransportCriteria
                departureCitiesOptions={departureCities}
                departureCity={departureCity}
                initialTransport={initialTransport}
                isJoyview={resort.isJoyview}
                transportIcon={transportIcon}
                withValidDepartureCity
              />
              <Text
                className="hidden px-5 md:mt-30 md:block"
                text={t('bookingConfig.form.legalNotice')}
                theme="text-sans-n2"
              />
            </div>
            <div className="md:flex-2">
              <ReassuranceBlock items={i18n?.booking?.form?.items || []} />
              <Text
                className="mt-20 md:hidden"
                text={t('bookingConfig.form.legalNotice')}
                theme="text-sans-n2"
              />
            </div>
          </div>
        </Fragment>
      )}

      <BookingStepTitle
        className="mb-15"
        number={withTransport ? 2 : 1}
        title={t('booking.form.resortCriteriaDatesSelectionTitle')}
      />
      {Children.map(children, (child) =>
        isValidElement(child)
          ? cloneElement(child, {
              handleChange: handleChange,
            })
          : child,
      )}

      {isWinterResort && isFeatureFlipped(appContext)('booking.reinsuranceSkiPass') && (
        <Notification
          className="mb-40 text-sienna"
          text={i18n?.skiPassIncludedDescription}
          title={i18n?.skiPassIncludedLabel}
        />
      )}

      <BookingStepTitle
        className="mb-15"
        number={withTransport ? 3 : 2}
        title={bookingConfig?.form?.guestCriteriaTitle}
      />
      <Participants
        {...asWidgetProps<React.ComponentProps<typeof Participants>>({
          adultAge: get(props, 'i18n.adultAge'),
          adultsLabel: get(props, 'i18n.common.participants.adultsLabel'),
          adultOnly: adultOnly,
          adultsNumber: adultsNumber,
          childAge: get(props, 'childAge'),
          childrenBirthdayDateFormatPlaceholder: get(
            props,
            'i18n.childrenBirthdayDateFormatPlaceholder',
            '',
          ),
          dateFormat: get(props, 'i18n.dateFormat'),
          dayPlaceholder: get(props, 'i18n.dayPlaceholder'),
          departureDate: get(props, 'departureDate'),
          kids: kids,
          kidsAgeLimit: get(props, 'i18n.common.participants.kidsAgeLimit'),
          kidsLabel: get(props, 'i18n.common.participants.kidsLabel'),
          minAgeInformation: get(props, 'i18n.minAgeInformation'),
          monthPlaceholder: get(props, 'i18n.monthPlaceholder'),
          selectAdultsLabels: get(props, 'i18n.selectAdultsLabels'),
          selectChildrenLabels: get(props, 'i18n.selectChildrenLabels'),
          setAdultsNumber: handleChange(props.setAdultsNumber),
          setKids: handleChange(({ kids: _kids, kidsValidations }) => {
            batch(() => {
              props.setKids(_kids);
              props.setKidsValidationState(kidsValidations);
            });
          }),
          yearPlaceholder: get(props, 'i18n.yearPlaceholder'),
        })}
      />

      {resort?.isResort && !!kids.length && (
        <Notification
          className="mt-20 text-saffron"
          text={`**${i18n.didYouKnowLabel}** ${i18n.kidStayIncludedDescription}`}
          title={i18n?.kidStayIncludedLabel}
        />
      )}

      <ParticipantsErrors
        className="mt-20 justify-end"
        config={pick(config.booking.form, ['errorMaxGuestsLabel', 'departureDateErrorMessage'])}
        errors={formErrors}
        isSubmitted={hasCheckedPrice}
      />

      <div className="mt-40 flex justify-end">
        <Button
          className="w-full md:w-auto"
          data-testid="BookingCriteria-checkPrice"
          iconRight={{
            name: ICONS.ARROW_RIGHT,
          }}
          isDisabled={isLoading || !!formErrors.maxPax}
          onClick={onSubmit}
        >
          {i18n?.checkPriceLabel}
        </Button>
      </div>
    </Fragment>
  );
};

export const BookingCriteria = ({
  myRef,
  ...otherProps
}: Props & { myRef?: React.Ref<unknown> }) => (
  <BookingCriteriaComponent
    {...asWidgetProps<
      ComponentProps<typeof BookingCriteriaComponent> & { ref?: React.Ref<unknown> }
    >({
      ref: myRef,
      ...otherProps,
    })}
  />
);
